/**
 * Which buttons a kiosk's service grid shows, and how many fit.
 *
 * A design chooses its services by id and in its own order, because "which button
 * is for which service" is the question a branch asks first and the catalogue's
 * order is head office's answer, not theirs. The design is organisation-wide and
 * branches differ, so a chosen service a branch does not offer is left out there
 * rather than drawn as a button that cannot issue anything.
 */

import { serviceName, type Language } from './kioskText'
import type { Service } from './types'

/** How tall a kiosk button is. A finger is wide, and a button that needs aim is a
 *  button that gets pressed twice. */
export const SERVICE_ROW = 150
/** The space between two buttons. Here, beside SERVICE_ROW, so that what the
 *  editor says a box holds and what the renderer draws in it are the same sum. */
export const SERVICE_GAP = 20

/**
 * The services a grid shows: the chosen ones in the chosen order, or every offered
 * one when nothing was chosen.
 *
 * When every chosen service has gone from this branch the answer is everything
 * offered rather than nothing: a lobby machine with no buttons at all is worse than
 * one with the catalogue's order.
 */
export function menuFor<T extends { id: string }>(offered: readonly T[], chosen: readonly string[] | undefined): T[] {
  if (!chosen || chosen.length === 0) return [...offered]
  const byId = new Map(offered.map((o) => [o.id, o]))
  const picked = chosen.map((id) => byId.get(id)).filter((o): o is T => o !== undefined)
  return picked.length > 0 ? picked : [...offered]
}

/** A service in the shape a design's preview draws it: only what a button needs. */
export type MenuService = { id: string; prefix: string; name: string }

/**
 * What a kiosk design's preview draws for its service grid: the organisation's
 * own catalogue, chosen and ordered exactly as menuFor answers for the real
 * screens, or the sample menu's invented names when there is no catalogue to
 * draw from.
 *
 * That fallback is the point of taking sample as a parameter rather than
 * reaching for sampleBindings itself: the editor and the gallery both draw this
 * preview, from the moment a kiosk design is picked, before the organisation's
 * catalogue has necessarily loaded, and a preview that showed nothing in that
 * gap would look broken rather than merely generic. Once the catalogue has
 * loaded, every active service in it replaces the sample regardless of what
 * the design has chosen — see menuFor for how a choice then narrows it.
 */
export function previewMenu(
  catalogue: readonly Service[] | undefined,
  chosen: readonly string[] | undefined,
  sample: readonly MenuService[],
  lang: Language = 'en',
  orgDefault?: Language,
): MenuService[] {
  const real = (catalogue ?? [])
    .filter((s) => s.active)
    .map((s) => ({ id: s.id, prefix: '', name: serviceName(s.name, lang, s.code, orgDefault) }))
  const offered = real.length > 0 ? real : sample
  return menuFor(offered, chosen)
}

/** How many buttons a box of this height holds, whole rows only. */
export function capacity(boxH: number, columns: number): number {
  const rows = Math.max(0, Math.floor((boxH + SERVICE_GAP) / (SERVICE_ROW + SERVICE_GAP)))
  return rows * Math.max(1, columns)
}
